import { AppPushState } from './AppPushState';
import { InstantPushConfigState } from './InstantPushConfigState';
import { InstantPushState } from './InstantPushState';
import { Constants } from '../common/Constants';
import * as InternalTypes from '../common/InternalTypes';
import { Logger } from '../common/Logger';
import { Util } from '../common/Util';

export class UserState {
	private _version = 1;
	private _isDebugState = false;
	private _lastPush: number;
	private _lastTheme: string;
	private _currentTheme: string;
	private _themeToggleStart: number;
	private _appPushStates: Map<string, AppPushState>;
	private _timedPushDate: number;
	private _appPushDate: number;
	private _snoozeCount = 0;
	private _instantPushState: InstantPushState;
	private readonly _logger: Logger;

	constructor(isDebugState = false) {
		this._isDebugState = isDebugState;
		this.appPushStates = new Map<string, AppPushState>();
		this._instantPushState = new InstantPushState();
		this._logger = new Logger(Constants.FILE_USER_STATE);
	}

	initFromJsonObject(userStateObject: InternalTypes.UserState) {
		this._isDebugState = !!userStateObject.isDebugState;

		if (userStateObject.version) {
			this._version = userStateObject.version;
		}
		if (userStateObject.lastPush) {
			this._lastPush = userStateObject.lastPush;
		}
		if (userStateObject.timedPushDate) {
			this._timedPushDate = userStateObject.timedPushDate;
		}
		if (userStateObject.appPushDate) {
			this._appPushDate = userStateObject.appPushDate;
		}
		if (userStateObject.lastTheme) {
			this._lastTheme = userStateObject.lastTheme;
		}
		if (userStateObject.currentTheme) {
			this._currentTheme = userStateObject.currentTheme;
		}
		if (userStateObject.toggleStart) {
			this._themeToggleStart = userStateObject.toggleStart;
		}
		if (userStateObject.snoozeCount) {
			this._snoozeCount = userStateObject.snoozeCount;
		}
		this.appPushStates = new Map<string, AppPushState>();
		if (userStateObject.appPushStates) {
			this._parseAppPushStates(userStateObject.appPushStates);
		}
		if (userStateObject.instantPush) {
			this._instantPushState.initFromJsonObject(userStateObject.instantPush);
		}
	}

	private _parseAppPushStates(currentAppPushStates: InternalTypes.AppPushStates) {
		const appPushStatesMap = new Map(Object.entries(currentAppPushStates));
		appPushStatesMap.forEach((_, key) => {
			try {
				const newAppPushState = new AppPushState();
				newAppPushState.initFromJsonObject(currentAppPushStates[key]);
				const combinedKey = newAppPushState.combinedKey;
				this.appPushStates.set(combinedKey, newAppPushState);
			} catch (error) {
				this._logger.error(`App Push state could not be parsed correctly, will be ignored. ${error.message}`);
			}
		});
	}

	get appPushStates() {
		return this._appPushStates;
	}

	set appPushStates(appPushStates) {
		this._appPushStates = appPushStates;
	}

	get instantPushState() {
		return this._instantPushState;
	}

	get isDebugState(): boolean {
		return this._isDebugState;
	}

	get lastPush(): number {
		return this._lastPush;
	}

	get timedPushDate(): number {
		return this._timedPushDate;
	}

	set timedPushDate(date: number) {
		this._timedPushDate = date;
	}

	get appPushDate(): number {
		return this._appPushDate;
	}

	set appPushDate(date: number) {
		this._appPushDate = date;
	}

	updateLastPushToNow() {
		this._lastPush = Date.now();
	}

	get lastTheme(): string {
		return this._lastTheme;
	}

	set lastTheme(themeId: string) {
		this._lastTheme = themeId;
	}

	get currentTheme(): string {
		return this._currentTheme;
	}

	set currentTheme(themeId: string) {
		this._currentTheme = themeId;
	}

	get themeToggleStart(): number {
		return this._themeToggleStart;
	}

	set themeToggleStart(date: number) {
		this._themeToggleStart = date;
	}

	clearThemeToggleStart() {
		this._themeToggleStart = undefined!;
	}

	get snoozeCount() {
		return this._snoozeCount;
	}

	increaseSnoozeCount() {
		this._snoozeCount++;
	}

	resetSnoozeCount() {
		this._snoozeCount = 0;
	}

	createAndAddAppPushState(areaId: string, triggerName: string, triggerType: string): AppPushState {
		const newAppPushState = new AppPushState();
		newAppPushState.init(areaId, triggerName, triggerType);
		this.addAppPushState(newAppPushState);
		return newAppPushState;
	}

	addAppPushState(appPushState: AppPushState) {
		this.appPushStates.set(appPushState.combinedKey, appPushState);
	}

	removeAppPushState(areaId: string, triggerName: string) {
		const combinedKey = Util.getCombinedKey(areaId, triggerName);
		if (this.appPushStates.has(combinedKey)) {
			this.appPushStates.delete(combinedKey);
		}
	}

	getAppPushState(areaId: string, triggerName: string): AppPushState | undefined {
		const combinedKey = Util.getCombinedKey(areaId, triggerName);
		return this.appPushStates.get(combinedKey);
	}

	findAppPushStatesReachedExpiryDate(): AppPushState[] | [] {
		const expiredAppStates: AppPushState[] = [];
		this.appPushStates.forEach((value) => {
			if (value.hasReachedExpiryDate()) {
				expiredAppStates.push(value);
			}
		});
		return expiredAppStates.length > 0 ? expiredAppStates : [];
	}

	createAndAddInstantPushState(areaId: string, triggerName: string, nextPushDateTime: number): InstantPushConfigState {
		const newInstantPushConfigState = new InstantPushConfigState();
		newInstantPushConfigState.init(areaId, triggerName, nextPushDateTime);
		this.instantPushState.addNewInstantPushConfig(newInstantPushConfigState.combinedKey, newInstantPushConfigState);
		return newInstantPushConfigState;
	}

	removeExistingInstantPushConfig(areaId: string, triggerName: string) {
		const combinedKey = Util.getCombinedKey(areaId, triggerName);
		this.instantPushState.removeExistingInstantPushConfig(combinedKey);
	}

	getInstantPushConfigState(areaId: string, triggerName: string): InstantPushConfigState | undefined {
		const combinedKey = Util.getCombinedKey(areaId, triggerName);
		return this.instantPushState.getInstantPushConfigState(combinedKey);
	}

	addNewTriggeredDateTime(newTriggeredDateTime: number) {
		this.instantPushState.addNewTriggeredDateTime(newTriggeredDateTime);
	}

	removeExistingTriggeredDateTime(existingTriggeredDateTime: number) {
		this.instantPushState.removeExistingTriggeredDateTime(existingTriggeredDateTime);
	}

	updateInstantPushNextPushDateTime(areaId: string, triggerName: string, nextPushDateTime: number) {
		const combinedKey = Util.getCombinedKey(areaId, triggerName);
		const instantPushConfig = this.instantPushState.getInstantPushConfigState(combinedKey);
		instantPushConfig?.updateNextPushDateTime(nextPushDateTime);
		this.addNewTriggeredDateTime(nextPushDateTime);
	}

	get userStateJson(): InternalTypes.UserState {
		const userStateObject = {} as InternalTypes.UserState;
		userStateObject.version = this._version;
		if (this._isDebugState) {
			userStateObject.isDebugState = this._isDebugState;
		}
		if (this.lastPush) {
			userStateObject.lastPush = this.lastPush;
		}
		if (this.timedPushDate) {
			userStateObject.timedPushDate = this.timedPushDate;
		}
		if (this.appPushDate) {
			userStateObject.appPushDate = this.appPushDate;
		}
		if (this.lastTheme) {
			userStateObject.lastTheme = this.lastTheme;
		}
		if (this.currentTheme) {
			userStateObject.currentTheme = this.currentTheme;
		}
		if (this.themeToggleStart) {
			userStateObject.toggleStart = this.themeToggleStart;
		}
		if (this.snoozeCount) {
			userStateObject.snoozeCount = this.snoozeCount;
		}
		if (this.appPushStates) {
			userStateObject.appPushStates = {};
			this.appPushStates.forEach((item, key) => {
				userStateObject.appPushStates![key] = item.appPushStateObject;
			});
		}
		if (this.instantPushState) {
			userStateObject.instantPush = this.instantPushState.instantPushJson;
		}
		return userStateObject;
	}
}
